import { useBlocker } from "@tanstack/react-router";
import type { RiskModelType } from "@unbox/db";
import { useEffect, useMemo, useState } from "react";
import {
  type MerchantRiskConfigEntry,
  useMerchantRiskCatalog,
  useMerchantRisks,
  useMerchantSettings,
  useSaveMerchantRisks,
} from "@/api/admin/merchant-risks";
import { useAllRisks, useCreateRisk, useDeleteRisk, useUpdateRisk } from "@/api/admin/risks";
import { useToast } from "@/hooks/use-toast";
import {
  buildConfigFromFields,
  defaultConfigFromFields,
  MERCHANT_CONFIG_FIELDS,
  serializeConfigForSave,
  slugifyRiskId,
} from "../risk-config-v2/config-fields";
import type { MerchantWithUsers } from "../types";
import type { DomainKnowledgeRow, ProductInfoData } from "./DomainKnowledge";
import { recordToRows, rowsToRecord } from "./DomainKnowledge";

export type CatalogRisk = {
  id: string;
  readableName: string;
  readableDescription: string;
  category: string;
  modelType: RiskModelType;
  defaultTier: number;
  config: Record<string, unknown> | null;
  // Local-only fields for custom risks the admin is creating but hasn't saved yet.
  // These don't exist in the `risks` table until the whole config is saved.
  isNew?: boolean;
  vision?: boolean;
};

export type RiskState = {
  enabled: boolean;
  tier: number;
  config: Record<string, unknown>;
  autoConfirm: boolean;
};

export type RisksState = Record<string, RiskState>;

type DefinitionEdit = Partial<Pick<CatalogRisk, "readableName" | "readableDescription">>;

export const useSafetyConfigState = (merchant: MerchantWithUsers) => {
  const { toast } = useToast();

  const { data: catalog, isLoading: isLoadingCatalog } = useMerchantRiskCatalog(merchant.id);
  const { data: merchantRisks, isLoading: isLoadingRisks } = useMerchantRisks(merchant.id);
  const { data: settings, isLoading: isLoadingSettings } = useMerchantSettings(merchant.id);
  const { data: allRisks } = useAllRisks();

  const saveRisks = useSaveMerchantRisks();
  const createRisk = useCreateRisk();
  const updateRiskMutation = useUpdateRisk();
  const deleteRiskMutation = useDeleteRisk();

  const [risksState, setRisksState] = useState<RisksState>({});
  // Pending edits to an already-persisted custom risk's catalog fields
  // (readableName / readableDescription). Keyed by the risk's real id. Staged
  // (isNew) risks edit `extraRisks` directly and never appear here.
  const [definitionEdits, setDefinitionEdits] = useState<Record<string, DefinitionEdit>>({});
  // Persisted custom risk queued for deletion; drives the confirmation dialog.
  const [riskToDelete, setRiskToDelete] = useState<CatalogRisk | null>(null);
  const [productInfo, setProductInfo] = useState<ProductInfoData>({ productName: "", productDescription: "" });
  const [domainKnowledgeRows, setDomainKnowledgeRows] = useState<DomainKnowledgeRow[]>([]);
  // Custom risks belonging to OTHER merchants that the admin has pulled in to add
  // to this merchant. Persisted (as merchant_risks rows) on save, after which they
  // fold into `catalog` via listForMerchant.
  const [extraRisks, setExtraRisks] = useState<CatalogRisk[]>([]);
  const [customSearch, setCustomSearch] = useState("");

  const [initial, setInitial] = useState<{
    risksState: RisksState;
    productInfo: ProductInfoData;
    domainKnowledgeRows: DomainKnowledgeRow[];
  } | null>(null);

  // Build form state once the catalog, merchant risks, and settings are loaded.
  // Re-runs on refetch; preserves any unsaved extra-risk selections.
  useEffect(() => {
    if (!catalog || !merchantRisks || isLoadingSettings) return;

    const byRiskId = new Map(merchantRisks.map((mr) => [mr.riskId, mr]));
    const fromCatalog: RisksState = {};
    for (const risk of catalog as CatalogRisk[]) {
      const mr = byRiskId.get(risk.id);
      const fields = MERCHANT_CONFIG_FIELDS[risk.modelType];
      fromCatalog[risk.id] = mr
        ? {
            enabled: true,
            tier: mr.tier ?? risk.defaultTier,
            config: buildConfigFromFields(fields, mr.config),
            autoConfirm: mr.autoConfirm ?? false,
          }
        : { enabled: false, tier: risk.defaultTier, config: defaultConfigFromFields(fields), autoConfirm: false };
    }

    const loadedProductInfo: ProductInfoData = {
      productName: settings?.productName ?? "",
      productDescription: settings?.productDescription ?? "",
    };
    const loadedDomainKnowledgeRows = recordToRows(settings?.domainKnowledge ?? undefined);

    const catalogIds = new Set((catalog as CatalogRisk[]).map((r) => r.id));
    // Drop extra risks that are now part of the catalog (i.e. just saved).
    setExtraRisks((prev) => prev.filter((r) => !catalogIds.has(r.id)));

    setRisksState((prevState) => {
      const next: RisksState = { ...fromCatalog };
      // Preserve in-progress selections for extra risks not yet in the catalog.
      for (const id of Object.keys(prevState)) {
        if (!next[id]) next[id] = prevState[id];
      }
      return next;
    });
    setProductInfo(loadedProductInfo);
    setDomainKnowledgeRows(loadedDomainKnowledgeRows);
    // `initial` reflects persisted server state only, so unsaved extra risks read as changes.
    setInitial({
      risksState: fromCatalog,
      productInfo: loadedProductInfo,
      domainKnowledgeRows: loadedDomainKnowledgeRows,
    });
  }, [catalog, merchantRisks, settings, isLoadingSettings]);

  const mergedCatalog = useMemo<CatalogRisk[]>(() => {
    const base = (catalog as CatalogRisk[] | undefined) ?? [];
    const ids = new Set(base.map((r) => r.id));
    return [...base, ...extraRisks.filter((r) => !ids.has(r.id))];
  }, [catalog, extraRisks]);

  // Ids of persisted custom risks whose name/description differ from the catalog.
  // Comparing against the catalog (not just presence in definitionEdits) means
  // typing then reverting a value leaves the page clean.
  const dirtyDefinitions = useMemo(() => {
    const byId = new Map(mergedCatalog.map((r) => [r.id, r]));
    return Object.keys(definitionEdits).filter((id) => {
      const risk = byId.get(id);
      if (!risk) return false;
      const edit = definitionEdits[id];
      return (
        (edit.readableName !== undefined && edit.readableName !== risk.readableName) ||
        (edit.readableDescription !== undefined && edit.readableDescription !== risk.readableDescription)
      );
    });
  }, [definitionEdits, mergedCatalog]);

  const hasChanges = useMemo(() => {
    if (!initial) return false;
    // Extra risks live in risksState but not in initial.risksState → counted here.
    return (
      dirtyDefinitions.length > 0 ||
      JSON.stringify(risksState) !== JSON.stringify(initial.risksState) ||
      JSON.stringify(productInfo) !== JSON.stringify(initial.productInfo) ||
      JSON.stringify(rowsToRecord(domainKnowledgeRows)) !== JSON.stringify(rowsToRecord(initial.domainKnowledgeRows))
    );
  }, [dirtyDefinitions, risksState, productInfo, domainKnowledgeRows, initial]);

  // Warn before losing unsaved edits — both on in-app navigation (router blocker
  // with a confirm dialog) and on browser refresh/close (native beforeunload).
  const blocker = useBlocker({
    shouldBlockFn: () => hasChanges,
    enableBeforeUnload: () => hasChanges,
    withResolver: true,
  });

  const updateRisk = (riskId: string, patch: Partial<RiskState>) => {
    setRisksState((prev) => ({ ...prev, [riskId]: { ...prev[riskId], ...patch } }));
  };

  const toggleRisk = (risk: CatalogRisk, enabled: boolean) => {
    // On first enable (no saved merchant config), inherit the catalog risk's own
    // config — e.g. a debate risk copies its description/hardcoded_rules template.
    // Optional fields (omni's threshold) are NOT inherited: absent means "use the
    // catalog default", and copying the default would pin it for this merchant.
    if (enabled && !initial?.risksState[risk.id]?.enabled) {
      updateRisk(risk.id, {
        enabled: true,
        config: buildConfigFromFields(
          MERCHANT_CONFIG_FIELDS[risk.modelType].filter((f) => !f.optional),
          risk.config,
        ),
      });
      return;
    }
    updateRisk(risk.id, { enabled });
  };

  // Custom risks from other merchants not yet visible to this one.
  const availableCustomRisks = useMemo<CatalogRisk[]>(() => {
    const visibleIds = new Set(mergedCatalog.map((r) => r.id));
    const list = ((allRisks as CatalogRisk[] | undefined) ?? []).filter(
      (r) => r.category === "custom" && !visibleIds.has(r.id),
    );
    const q = customSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => r.readableName.toLowerCase().includes(q) || r.id.toLowerCase().includes(q));
  }, [allRisks, mergedCatalog, customSearch]);

  const addCustomRisk = (risk: CatalogRisk) => {
    setExtraRisks((prev) => (prev.some((r) => r.id === risk.id) ? prev : [...prev, risk]));
    setRisksState((prev) => ({
      ...prev,
      [risk.id]: {
        enabled: true,
        tier: risk.defaultTier,
        config: buildConfigFromFields(MERCHANT_CONFIG_FIELDS[risk.modelType], risk.config),
        autoConfirm: false,
      },
    }));
  };

  // Autocomplete options: matching custom risks from other merchants. Creating a
  // brand-new custom risk is a separate explicit action (the "Create new" button).
  const customRiskOptions = useMemo(
    () =>
      availableCustomRisks.slice(0, 50).map((risk) => ({ id: risk.id, label: `${risk.id} (${risk.readableName})` })),
    [availableCustomRisks],
  );

  // Stage a brand-new custom risk locally. It's not persisted to the `risks` table
  // until the whole config is saved (see handleSave) — "Create new" only adds an
  // editable row whose name (and therefore id) stays editable until save.
  //
  // Staged risks are keyed by a stable local key, NOT the slugified id: the id is
  // derived from the (editable) name at save time, so keying by it would re-mount
  // the name input on every keystroke and drop focus. Returns the local key so
  // the caller can open the new row's config accordion.
  const addNewCustomRisk = (name: string): string => {
    const localKey = `__new_${crypto.randomUUID()}`;
    // Custom risks are LLM-evaluated (debate).
    const newRisk: CatalogRisk = {
      id: localKey,
      readableName: name,
      readableDescription: "",
      category: "custom",
      modelType: "debate",
      defaultTier: 1,
      config: null,
      isNew: true,
      vision: false,
    };
    setExtraRisks((prev) => [...prev, newRisk]);
    setRisksState((prev) => ({
      ...prev,
      [localKey]: {
        enabled: true,
        tier: newRisk.defaultTier,
        config: buildConfigFromFields(MERCHANT_CONFIG_FIELDS[newRisk.modelType], newRisk.config),
        autoConfirm: false,
      },
    }));
    setCustomSearch("");
    return localKey;
  };

  const setNewCustomRiskName = (localKey: string, readableName: string) => {
    setExtraRisks((prev) => prev.map((r) => (r.id === localKey ? { ...r, readableName } : r)));
  };

  const setNewCustomRiskVision = (localKey: string, vision: boolean) => {
    setExtraRisks((prev) => prev.map((r) => (r.id === localKey ? { ...r, vision } : r)));
  };

  const setNewCustomRiskReadableDescription = (localKey: string, readableDescription: string) => {
    setExtraRisks((prev) => prev.map((r) => (r.id === localKey ? { ...r, readableDescription } : r)));
  };

  const removeNewCustomRisk = (localKey: string) => {
    setExtraRisks((prev) => prev.filter((r) => r.id !== localKey));
    setRisksState((prev) => {
      const next = { ...prev };
      delete next[localKey];
      return next;
    });
  };

  // Catalog fields (name/description) of an already-persisted custom risk, with
  // any unsaved edits applied on top. Staged (isNew) risks have no definitionEdits
  // entry, so this is a no-op for them.
  const effectiveRisk = (risk: CatalogRisk): CatalogRisk => ({ ...risk, ...(definitionEdits[risk.id] ?? {}) });

  const setCustomRiskName = (risk: CatalogRisk, readableName: string) => {
    if (risk.isNew) {
      setNewCustomRiskName(risk.id, readableName);
      return;
    }
    setDefinitionEdits((prev) => ({ ...prev, [risk.id]: { ...prev[risk.id], readableName } }));
  };

  const setCustomRiskReadableDescription = (risk: CatalogRisk, readableDescription: string) => {
    if (risk.isNew) {
      setNewCustomRiskReadableDescription(risk.id, readableDescription);
      return;
    }
    setDefinitionEdits((prev) => ({ ...prev, [risk.id]: { ...prev[risk.id], readableDescription } }));
  };

  const handleCustomRiskSelect = (key: string | number | null) => {
    if (key == null) return;
    const risk = availableCustomRisks.find((r) => r.id === key);
    if (risk) addCustomRisk(risk);
    setCustomSearch("");
  };

  const handleCancel = () => {
    if (!initial) return;
    setRisksState(initial.risksState);
    setExtraRisks([]);
    setDefinitionEdits({});
    setProductInfo(initial.productInfo);
    setDomainKnowledgeRows(initial.domainKnowledgeRows);
  };

  const handleSave = async () => {
    if (!initial) return;

    const enabled: MerchantRiskConfigEntry[] = [];
    const updated: MerchantRiskConfigEntry[] = [];
    const disabled: string[] = [];

    for (const risk of mergedCatalog) {
      if (risk.isNew) continue; // staged new customs are handled separately below
      const cur = risksState[risk.id];
      if (!cur) continue;
      const init = initial.risksState[risk.id]; // undefined for added extra risks

      if (cur.enabled && !init?.enabled) {
        enabled.push({
          riskId: risk.id,
          tier: cur.tier,
          config: serializeConfigForSave(cur.config),
          autoConfirm: cur.autoConfirm,
        });
      } else if (!cur.enabled && init?.enabled) {
        disabled.push(risk.id);
      } else if (cur.enabled && init?.enabled) {
        const changed =
          cur.tier !== init.tier ||
          cur.autoConfirm !== init.autoConfirm ||
          JSON.stringify(cur.config) !== JSON.stringify(init.config);
        if (changed) {
          updated.push({
            riskId: risk.id,
            tier: cur.tier,
            config: serializeConfigForSave(cur.config),
            autoConfirm: cur.autoConfirm,
          });
        }
      }
    }

    // New custom risks staged via addNewCustomRisk only exist locally, keyed by a
    // local key. The persisted id is the slugified (editable) name, resolved here.
    //
    // Only enabled staged risks are validated/persisted: the description and remove
    // controls are only rendered while a row is enabled, so a disabled staged risk
    // has no visible way to fix or discard it. Treating it like a no-op (skip
    // validation, don't create) avoids blocking save on a row the admin can no
    // longer see or edit.
    const newCustomRisks = mergedCatalog.filter((r) => r.isNew && risksState[r.id]?.enabled);

    // Validate names up front so we never half-create a batch (each createRisk is
    // its own transaction). Names/ids must be present and unique.
    const takenIds = new Set([
      ...mergedCatalog.filter((r) => !r.isNew).map((r) => r.id),
      ...((allRisks as CatalogRisk[] | undefined) ?? []).map((r) => r.id),
    ]);
    const newRiskIds = new Map<string, string>(); // localKey -> slug id
    for (const risk of newCustomRisks) {
      const id = slugifyRiskId(risk.readableName);
      if (!id) {
        toast({ title: "Error", description: "Custom risks need a name", variant: "destructive" });
        return;
      }
      if (!risk.readableDescription?.trim()) {
        toast({ title: "Error", description: "Custom risks need a user facing description", variant: "destructive" });
        return;
      }
      if (takenIds.has(id)) {
        toast({ title: "Error", description: `A risk with id "${id}" already exists`, variant: "destructive" });
        return;
      }
      takenIds.add(id);
      newRiskIds.set(risk.id, id);
    }

    // Name/description edits to already-persisted custom risks. Validated up
    // front (same rules/toasts as creation) and applied via PATCH before the
    // bulk config save below. The risk id is immutable, so only the display
    // fields change here.
    const catalogById = new Map(mergedCatalog.map((r) => [r.id, r]));
    const definitionPatches: { id: string; patch: { readableName: string; readableDescription: string } }[] = [];
    for (const id of dirtyDefinitions) {
      const risk = catalogById.get(id);
      if (!risk) continue;
      const eff = effectiveRisk(risk);
      if (!eff.readableName?.trim()) {
        toast({ title: "Error", description: "Custom risks need a name", variant: "destructive" });
        return;
      }
      if (!eff.readableDescription?.trim()) {
        toast({ title: "Error", description: "Custom risks need a user facing description", variant: "destructive" });
        return;
      }
      definitionPatches.push({
        id,
        patch: { readableName: eff.readableName, readableDescription: eff.readableDescription },
      });
    }

    try {
      for (const { id, patch } of definitionPatches) {
        await updateRiskMutation.mutateAsync({ id, patch });
      }

      // Persist each staged risk to the `risks` table first (no merchantIds — the
      // `enabled` bucket below creates the merchant_risks row with the tier/config
      // the admin set), so bulkEnable can resolve it.
      for (const risk of newCustomRisks) {
        const id = newRiskIds.get(risk.id)!;
        const cur = risksState[risk.id];
        await createRisk.mutateAsync({
          id,
          readableName: risk.readableName,
          readableDescription: risk.readableDescription,
          category: "custom",
          modelType: "debate",
          defaultTier: risk.defaultTier,
          vision: risk.vision ?? false,
          // Default debate config so the catalog row is never persisted empty; the
          // merchant_risks row carries the admin's per-merchant values.
          config: { hardcoded_rules: "", description: "", confidence_threshold: 0.85 },
        });
        if (cur?.enabled) {
          enabled.push({
            riskId: id,
            tier: cur.tier,
            config: serializeConfigForSave(cur.config),
            autoConfirm: cur.autoConfirm,
          });
        }
      }

      // Risks and settings save together in one transaction (settings upserted
      // first) so risk placeholder validation sees the new domain knowledge and
      // there's no save-order race.
      await saveRisks.mutateAsync({
        merchantId: merchant.id,
        enabled,
        updated,
        disabled,
        settings: {
          productName: productInfo.productName || null,
          productDescription: productInfo.productDescription || null,
          domainKnowledge: rowsToRecord(domainKnowledgeRows),
        },
      });

      // Drop the locally-keyed staged risks; the catalog refetch (invalidated by the
      // save) re-adds them keyed by their real (slug) id. Snapshot the cleaned state
      // as the new baseline so there are no phantom "unsaved changes".
      const stagedKeys = new Set(newCustomRisks.map((r) => r.id));
      setExtraRisks((prev) => prev.filter((r) => !stagedKeys.has(r.id)));
      const cleanedRisksState: RisksState = {};
      for (const [key, value] of Object.entries(risksState)) {
        if (!stagedKeys.has(key)) cleanedRisksState[key] = value;
      }
      setRisksState(cleanedRisksState);
      setDefinitionEdits({});
      setInitial({ risksState: cleanedRisksState, productInfo, domainKnowledgeRows });
      toast({ title: "Success", description: "Risk configuration saved" });
    } catch {
      // Errors are surfaced by the global mutation error toast.
    }
  };

  const handleConfirmDelete = async () => {
    if (!riskToDelete) return;
    const risk = riskToDelete;
    try {
      const { deleted } = await deleteRiskMutation.mutateAsync({ id: risk.id, merchantId: merchant.id });
      // Drop it from local state so it isn't re-sent in the `disabled` bucket on
      // the next save or counted as an unsaved change; the catalog refetch
      // (invalidated by the delete) reconciles the rest.
      setRisksState((prev) => {
        const next = { ...prev };
        delete next[risk.id];
        return next;
      });
      setInitial((prev) => {
        if (!prev) return prev;
        const nextRisks = { ...prev.risksState };
        delete nextRisks[risk.id];
        return { ...prev, risksState: nextRisks };
      });
      setDefinitionEdits((prev) => {
        const next = { ...prev };
        delete next[risk.id];
        return next;
      });
      toast({
        title: "Success",
        description: deleted ? "Custom risk deleted" : "Removed from this merchant (still used by others)",
      });
    } catch {
      // Errors are surfaced by the global mutation error toast.
    } finally {
      setRiskToDelete(null);
    }
  };

  const isLoading = isLoadingCatalog || isLoadingRisks || isLoadingSettings;
  const enabledCount = Object.values(risksState).filter((r) => r.enabled).length;

  return {
    isLoading,
    initial,
    settings,
    mergedCatalog,
    risksState,
    hasChanges,
    isPending: saveRisks.isPending,
    isDeletePending: deleteRiskMutation.isPending,
    enabledCount,
    blocker,
    riskToDelete,
    setRiskToDelete,
    productInfo,
    setProductInfo,
    domainKnowledgeRows,
    setDomainKnowledgeRows,
    customSearch,
    setCustomSearch,
    customRiskOptions,
    updateRisk,
    toggleRisk,
    addNewCustomRisk,
    handleCustomRiskSelect,
    setNewCustomRiskVision,
    removeNewCustomRisk,
    effectiveRisk,
    setCustomRiskName,
    setCustomRiskReadableDescription,
    handleCancel,
    handleSave,
    handleConfirmDelete,
  };
};
